// Multi-unit inventory helpers.
//
// Storage model (Option A — base-unit canonical):
//   Each item has a base unit (e.g. "Pcs"). All stockFlow rows store
//   `baseQuantity` and `baseUnitCost` so WAVG, balances, and ledger remain
//   coherent regardless of which unit the line was entered in.
//
//   Items can optionally carry a `units[]` array:
//     [
//       { unitCode: 'Pcs', isBase: true,  conversion: 1,  salePrice, purchasePrice, barcode },
//       { unitCode: 'Ctn', isBase: false, conversion: 24, salePrice, purchasePrice, barcode },
//     ]
//   `conversion` = how many BASE units in one of this unit. Base unit is always 1.
//
// Backward compatibility: if `units` is empty/missing, item falls back to the
// single `unitOfMeasure` field — every existing line behaves as base-unit.

/**
 * Get the base unit code for an item.
 * Falls back to `unitOfMeasure` for items without a `units[]` array.
 */
export function getBaseUnit(item) {
  if (!item) return '';
  if (Array.isArray(item.units) && item.units.length > 0) {
    const base = item.units.find(u => u.isBase) || item.units[0];
    return base?.unitCode || item.unitOfMeasure || '';
  }
  return item.unitOfMeasure || '';
}

/**
 * Resolve a unit row by code. Returns null if not found.
 * Single-unit items return a synthetic base row when their `unitOfMeasure` matches.
 */
export function resolveUnit(item, unitCode) {
  if (!item || !unitCode) return null;
  if (Array.isArray(item.units) && item.units.length > 0) {
    return item.units.find(u => u.unitCode === unitCode) || null;
  }
  // Single-unit fallback
  if (item.unitOfMeasure === unitCode) {
    return {
      unitCode,
      isBase: true,
      conversion: 1,
      salePrice: Number(item.salePrice) || 0,
      purchasePrice: Number(item.purchasePrice) || 0,
      barcode: item.barcode || ''
    };
  }
  return null;
}

/**
 * Conversion factor from a chosen unit to the item's base unit.
 * E.g. 1 Ctn (conversion=24) → 24 base Pcs.
 * Returns 1 when unit can't be resolved (safe default — line stays in base).
 */
export function conversionFactor(item, unitCode) {
  const u = resolveUnit(item, unitCode);
  if (!u) return 1;
  const c = Number(u.conversion);
  return Number.isFinite(c) && c > 0 ? c : 1;
}

/**
 * Convert a quantity from a chosen unit to base units.
 * baseQty = qty × conversion.
 */
export function toBaseQty(item, qty, unitCode) {
  const factor = conversionFactor(item, unitCode);
  return (Number(qty) || 0) * factor;
}

/**
 * Convert a base-unit quantity back to a target unit.
 * targetQty = baseQty / conversion. Returns NaN-safe number.
 */
export function fromBaseQty(item, baseQty, unitCode) {
  const factor = conversionFactor(item, unitCode);
  if (factor === 0) return 0;
  return (Number(baseQty) || 0) / factor;
}

function formatNumber(n) {
  const v = Number(n) || 0;
  if (Number.isInteger(v)) return v.toString();
  return v.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

/**
 * Format just the qty portion (no unit suffix) but honoring entered values.
 * Useful for table cells where the unit is already shown separately.
 * Returns "10 (240 Pcs)" or "10" depending on row content.
 */
export function formatStockFlowQtyCell(row) {
  if (!row) return '';
  const baseQty = Number(row.quantity) || 0;
  const enteredQty = row.enteredQuantity != null ? Number(row.enteredQuantity) : null;
  const enteredUnit = row.enteredUnitCode || '';
  const baseUnit = row.unitOfMeasure || '';
  const conversion = Number(row.conversion) || 1;
  // Show Two Units: "240 Mtr (10 Ctn)"; base qty added when neither unit is base.
  if (row.secondUnitCode && enteredQty != null && enteredUnit) {
    const two = `${formatNumber(enteredQty)} ${enteredUnit} (${formatNumber(row.secondQuantity)} ${row.secondUnitCode})`;
    return (enteredUnit === baseUnit || row.secondUnitCode === baseUnit) ? two : `${two} = ${formatNumber(baseQty)} ${baseUnit}`;
  }
  if (enteredQty != null && enteredUnit && enteredUnit !== baseUnit && conversion > 1) {
    return `${formatNumber(enteredQty)} ${enteredUnit} (${formatNumber(baseQty)} ${baseUnit})`;
  }
  return formatNumber(baseQty);
}

/**
 * Enrich a transaction line with multi-unit fields.
 * Adds: unitCode, conversion, baseQuantity, baseUnitPrice, baseUnitCost.
 * Pass-through if item has no `units[]` (line stays as-is, base = entered).
 *
 * Used by transactionHelper / stockFlow writer so downstream WAVG and
 * inventory balances always read the base-unit fields.
 */
/**
 * Seed a fresh line with the item's default unit + per-unit pricing.
 * Returns a partial object the caller spreads into the line.
 *
 * `priceField`: 'salePrice' for sales forms, 'purchasePrice' for purchase forms.
 * `costField`: 'purchasePrice' for sales (COGS), 'purchasePrice' for purchases (same).
 *
 * If item has no `units[]`, falls back to legacy unitOfMeasure + base salePrice/purchasePrice.
 */
export function seedLineUnitFromItem(item, { priceField = 'salePrice', defaultUnitField = 'defaultSaleUnit', costFromAverage = false } = {}) {
  if (!item) return { unitCode: '', unitOfMeasure: '', unitPrice: 0, unitCost: 0 };
  const baseSalePrice = Number(item.salePrice) || 0;
  const basePurchasePrice = Number(item.purchasePrice) || Number(item.averageCost) || 0;
  // INV-04: sale screens pass costFromAverage → cost of goods sold uses the
  // weighted average first (item-card purchase price only when no average yet).
  // Purchase/stock screens are unchanged. Prices are never affected.
  const avgCost = Number(item.averageCost) || 0;
  const baseUnitCode = item.unitOfMeasure || '';

  if (!Array.isArray(item.units) || item.units.length === 0) {
    return {
      unitCode: baseUnitCode,
      unitOfMeasure: baseUnitCode,
      unitPrice: priceField === 'salePrice' ? baseSalePrice : basePurchasePrice,
      unitCost: (costFromAverage && avgCost) || basePurchasePrice
    };
  }

  const baseRow = item.units.find(u => u.isBase) || item.units[0];
  const defaultCode = item[defaultUnitField];
  const row = (defaultCode && item.units.find(u => u.unitCode === defaultCode)) || baseRow;
  const conv = Number(row.conversion) || 1;
  const unitSale = Number(row.salePrice) || baseSalePrice * conv;
  const unitPurchase = Number(row.purchasePrice) || basePurchasePrice * conv;
  return {
    unitCode: row.unitCode,
    unitOfMeasure: row.unitCode,
    unitPrice: priceField === 'salePrice' ? unitSale : unitPurchase,
    unitCost: (costFromAverage && Number(item.averageCost)) ? Number(item.averageCost) * conv : unitPurchase // INV-04 (sales only)
  };
}

/**
 * Apply a unit-code change to an existing line.
 * Returns a partial object the caller spreads into the updated line.
 *
 * `priceField`: 'salePrice' (sales) or 'purchasePrice' (purchases).
 *
 * Snaps unitPrice/unitCost to the chosen unit's per-unit pricing if defined,
 * otherwise scales base price by the conversion factor.
 */
export function applyUnitChangeToLine(item, unitCode, { priceField = 'salePrice', costFromAverage = false } = {}) {
  if (!item || !Array.isArray(item.units)) return { unitCode, unitOfMeasure: unitCode };
  const row = item.units.find(u => u.unitCode === unitCode);
  if (!row) return { unitCode, unitOfMeasure: unitCode };
  const conv = Number(row.conversion) || 1;
  const baseSale = Number(item.salePrice) || 0;
  const basePurchase = Number(item.purchasePrice) || Number(item.averageCost) || 0;
  const unitSale = Number(row.salePrice) || baseSale * conv;
  const unitPurchase = Number(row.purchasePrice) || basePurchase * conv;
  return {
    unitCode: row.unitCode,
    unitOfMeasure: row.unitCode,
    unitPrice: priceField === 'salePrice' ? unitSale : unitPurchase,
    unitCost: (costFromAverage && Number(item.averageCost)) ? Number(item.averageCost) * conv : unitPurchase // INV-04 (sales only)
  };
}

export function enrichLineWithUnit(item, line) {
  if (!line) return line;
  const baseUnit = getBaseUnit(item);
  const unitCode = line.unitCode || line.unitOfMeasure || baseUnit;
  const factor = conversionFactor(item, unitCode);
  const qty = Number(line.quantity) || 0;
  const unitPrice = Number(line.unitPrice) || 0;
  const unitCost = Number(line.unitCost) || 0;
  return {
    ...line,
    unitCode,
    baseUnitCode: baseUnit,
    conversion: factor,
    baseQuantity: qty * factor,
    baseUnitPrice: factor > 0 ? unitPrice / factor : 0,
    baseUnitCost: factor > 0 ? unitCost / factor : 0,
  };
}

// ── Show Two Units (module `item_two_units`, 2026-10-10) ─────────────────────
// An item can pick two of its units to show together on sale/purchase lines:
//   item.showUnits = ['Ctn', 'Mtr']
// The line's own unit (line.unitCode) is the "Rate per" unit — price and
// amount work exactly as before. A second qty box shows the same quantity in
// the other unit of the pair, following the item's fixed conversion (decimals
// for part quantities). Typing in either box fills the other. Stock and
// costing are unchanged (always base unit via baseQuantity).

/** True when the company has "Show Two Units" on (set by AuthContext). */
export function twoUnitsModuleOn() {
  return typeof window !== 'undefined' && window.__AMAL_TWO_UNITS__ === true;
}

/** The item's valid two-unit pair ['A','B'] or null. */
export function getTwoUnitPair(item) {
  if (!item || !Array.isArray(item.units) || item.units.length < 2) return null;
  const pair = Array.isArray(item.showUnits) ? item.showUnits : null;
  if (!pair || pair.length !== 2) return null;
  const [a, b] = pair;
  if (!a || !b || a === b) return null;
  if (!item.units.some(u => u.unitCode === a) || !item.units.some(u => u.unitCode === b)) return null;
  return [a, b];
}

/**
 * The second unit to show for a line entered in `lineUnit`: the pair member
 * that is not the line's unit. Null when the module is off or the item has
 * no pair.
 */
export function secondUnitForLine(item, lineUnit) {
  if (!twoUnitsModuleOn()) return null;
  const pair = getTwoUnitPair(item);
  if (!pair) return null;
  return pair[0] !== lineUnit ? pair[0] : pair[1];
}

function roundTo(n, dp) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return parseFloat(v.toFixed(dp));
}

/** Qty in `lineUnit` → same quantity in `secondUnit` (4 decimals). */
export function secondQtyFromQty(item, qty, lineUnit, secondUnit) {
  const from = conversionFactor(item, lineUnit);
  const to = conversionFactor(item, secondUnit);
  if (!to) return 0;
  return roundTo((Number(qty) || 0) * from / to, 4);
}

/**
 * Qty typed in `secondUnit` → line qty in `lineUnit`. Kept to 9 decimals so
 * qty × rate gives the exact amount (e.g. 250 Mtr at 24 Mtr/Ctn → 10.416666667 Ctn).
 */
export function qtyFromSecondQty(item, qty2, lineUnit, secondUnit) {
  const from = conversionFactor(item, secondUnit);
  const to = conversionFactor(item, lineUnit);
  if (!to) return 0;
  return roundTo((Number(qty2) || 0) * from / to, 9);
}

/**
 * When the user switches a line's "Rate per" unit between the two units of the
 * pair, keep the same physical quantity (10 Ctn → 240 Mtr). Returns the new
 * qty, or null when this doesn't apply (module off, no pair, other units).
 */
export function twoUnitSwitchQty(item, oldUnit, newUnit, qty) {
  if (!twoUnitsModuleOn() || !oldUnit || !newUnit || oldUnit === newUnit) return null;
  const pair = getTwoUnitPair(item);
  if (!pair || !pair.includes(oldUnit) || !pair.includes(newUnit)) return null;
  const q = Number(qty) || 0;
  if (!q) return null;
  return qtyFromSecondQty(item, q, newUnit, oldUnit);
}

/**
 * Fields saved on a line so prints/views can show both units:
 * { secondUnitCode, secondQuantity } — or null when not applicable.
 */
export function twoUnitLineFields(item, line) {
  if (!line) return null;
  const lineUnit = line.unitCode || line.unitOfMeasure || getBaseUnit(item);
  const second = secondUnitForLine(item, lineUnit);
  if (!second || line.hasSize) return null;
  return { secondUnitCode: second, secondQuantity: secondQtyFromQty(item, line.quantity, lineUnit, second) };
}

/**
 * Base qty of a two-unit line, cleaned of conversion leftovers
 * (10.416666667 Ctn × 24 = 250.000000008 → 250 Mtr), so stock checks and
 * stock rows come out exact. 6 decimals.
 */
export function roundBaseQty(n) {
  return roundTo(n, 6);
}

/** Display a quantity with up to 4 decimals, no trailing zeros. */
export function formatTwoUnitQty(n) {
  const v = roundTo(n, 4);
  return String(v);
}
